// Entry point → Sign Up / Login Screen
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  Eye,
  EyeOff,
  Check,
  Package,
  TrendingUp,
  Users,
  ShoppingBag,
  Upload,
  X,
  KeyRound,
} from 'lucide-react';

import AppFooter from '@/components/AppFooter';
import { storeAuth, getStoredToken, clearAuth } from '@/lib/authStore';
import { authApi, statsApi } from '@/lib/api';
import { formatINR } from '@/lib/format';
import { Toaster, toast } from 'sonner';

// ─── Forgot Password Modal ──────────────────────────────────
// 2 steps: enter email (sends a code) → enter code + new password.
// Reuses the same email-OTP pattern as registration/admin login.
export function ForgotPasswordModal({ onClose }) {
  const [step, setStep] = useState('email'); // 'email' | 'reset' | 'done'
  const [email, setEmail] = useState('');
  const [session, setSession] = useState(null); // { resetId, otpId, masked }
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);

  const handleRequestCode = async (e) => {
    e.preventDefault();
    setError('');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Enter a valid email address.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await authApi.forgotPasswordStart({ email });
      setSession(res);
      setStep('reset');
      toast.success('Reset code sent via email.');
    } catch (err) {
      setError(err.message || 'Could not send a reset code.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      const res = await authApi.forgotPasswordResend({ resetId: session.resetId });
      setSession((prev) => ({ ...prev, otpId: res.otpId, masked: res.masked }));
      toast.success('New code sent.');
    } catch (err) {
      toast.error(err.message || 'Could not resend code.');
    } finally {
      setResending(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setError('');
    if (code.length !== 6) {
      setError('Enter the 6-digit code.');
      return;
    }
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      await authApi.forgotPasswordReset({
        resetId: session.resetId,
        otpId: session.otpId,
        code,
        newPassword,
      });
      setStep('done');
    } catch (err) {
      setError(err.message || 'Could not reset password.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center modal-overlay">
      <div className="w-full max-w-sm mx-4 paper-texture border-2 border-foreground ink-shadow" style={{ animation: 'fadeIn 150ms ease' }}>
        <div className="flex items-center justify-between p-4 border-b-2 border-foreground">
          <div className="flex items-center gap-2">
            <KeyRound size={16} style={{ color: 'var(--primary)' }} />
            <h3 className="font-heading text-base tracking-stamp" style={{ fontFamily: 'var(--font-heading)' }}>
              RESET PASSWORD
            </h3>
          </div>
          <button onClick={onClose} className="p-1 hover:opacity-70 transition-opacity">
            <X size={16} />
          </button>
        </div>

        <div className="p-4">
          {error && (
            <div
              className="px-3 py-2 border text-sm mb-4"
              style={{ backgroundColor: 'var(--danger-bg)', borderColor: 'var(--danger)', color: 'var(--danger)' }}
            >
              {error}
            </div>
          )}

          {step === 'email' && (
            <form onSubmit={handleRequestCode} className="flex flex-col gap-4">
              <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
                Enter the email on your account and we'll send you a 6-digit code to reset your password.
              </p>
              <div>
                <label className="block text-xs font-heading tracking-stamp mb-1" style={{ fontFamily: 'var(--font-heading)' }}>
                  EMAIL ADDRESS <span style={{ color: 'var(--primary)' }}>*</span>
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-ledger w-full px-3 py-2.5 text-sm"
                  placeholder="your@company.in"
                  autoFocus
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="btn-primary px-6 py-3 text-sm flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {submitting ? (
                  <>
                    <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    SENDING...
                  </>
                ) : (
                  'SEND RESET CODE'
                )}
              </button>
            </form>
          )}

          {step === 'reset' && (
            <form onSubmit={handleReset} className="flex flex-col gap-4">
              <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
                Enter the code sent to <strong>{session?.masked}</strong> and choose a new password.
              </p>

              <div>
                <label className="block text-xs font-heading tracking-stamp mb-1" style={{ fontFamily: 'var(--font-heading)' }}>
                  EMAIL CODE
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="input-ledger flex-1 px-3 py-2.5 text-sm font-mono-data tracking-widest"
                    style={{ fontFamily: 'var(--font-mono)' }}
                    placeholder="000000"
                  />
                  <button
                    type="button"
                    onClick={handleResend}
                    disabled={resending}
                    className="btn-secondary px-3 py-2 text-xs whitespace-nowrap disabled:opacity-60"
                  >
                    {resending ? 'SENDING...' : 'RESEND'}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs font-heading tracking-stamp mb-1" style={{ fontFamily: 'var(--font-heading)' }}>
                  NEW PASSWORD <span style={{ color: 'var(--primary)' }}>*</span>
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="input-ledger w-full px-3 py-2.5 text-sm pr-10"
                    placeholder="At least 8 characters"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs font-heading tracking-stamp mb-1" style={{ fontFamily: 'var(--font-heading)' }}>
                  CONFIRM NEW PASSWORD <span style={{ color: 'var(--primary)' }}>*</span>
                </label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="input-ledger w-full px-3 py-2.5 text-sm"
                  placeholder="Re-enter new password"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="btn-primary px-6 py-3 text-sm flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {submitting ? (
                  <>
                    <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    RESETTING...
                  </>
                ) : (
                  'RESET PASSWORD'
                )}
              </button>

              <button type="button" onClick={() => setStep('email')} className="text-xs underline self-start" style={{ color: 'var(--muted-foreground)' }}>
                ← Use a different email
              </button>
            </form>
          )}

          {step === 'done' && (
            <div className="flex flex-col items-center gap-3 py-4 text-center">
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--success-bg)' }}>
                <Check size={20} style={{ color: 'var(--secondary)' }} />
              </div>
              <p className="text-sm font-medium">Password updated.</p>
              <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
                You can now sign in with your new password.
              </p>
              <button onClick={onClose} className="btn-primary px-6 py-2.5 text-sm mt-2">
                BACK TO SIGN IN
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Login Form ───────────────────────────────────────────
function LoginForm() {
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm();

  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  const onSubmit = async (data) => {
    setLoginError('');

    try {
      const { token, user } = await authApi.login({
        email: data.email,
        password: data.password,
      });

      storeAuth(token, user);

      toast.success(`Welcome back, ${user.name.split(' ')[0]}!`);

      setTimeout(() => navigate('/dashboard'), 400);
    } catch (err) {
      setLoginError(err.message || 'Invalid credentials.');
    }
  };

  return (
    <>
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex flex-col gap-4"
    >
      {loginError && (
        <div
          className="px-3 py-2 border text-sm"
          style={{
            backgroundColor: 'var(--danger-bg)',
            borderColor: 'var(--danger)',
            color: 'var(--danger)',
          }}
        >
          {loginError}
        </div>
      )}

      <div>
        <label
          className="block text-xs font-heading tracking-stamp mb-1"
          style={{
            fontFamily: 'var(--font-heading)',
          }}
        >
          EMAIL ADDRESS{' '}
          <span style={{ color: 'var(--primary)' }}>*</span>
        </label>

        <input
          type="email"
          className="input-ledger w-full px-3 py-2.5 text-sm"
          placeholder="your@company.in"
          {...register('email', {
            required: 'Email is required',
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: 'Enter a valid email',
            },
          })}
        />

        {errors.email && (
          <p
            className="text-xs mt-1"
            style={{ color: 'var(--danger)' }}
          >
            {errors.email.message}
          </p>
        )}
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <label
            className="block text-xs font-heading tracking-stamp"
            style={{
              fontFamily: 'var(--font-heading)',
            }}
          >
            PASSWORD{' '}
            <span style={{ color: 'var(--primary)' }}>*</span>
          </label>
          <button
            type="button"
            onClick={() => setShowForgotPassword(true)}
            className="text-xs underline"
            style={{ color: 'var(--primary)' }}
          >
            Forgot password?
          </button>
        </div>

        <div className="relative">
          <input
            type={showPassword ? 'text' : 'password'}
            className="input-ledger w-full px-3 py-2.5 text-sm pr-10"
            placeholder="••••••••"
            {...register('password', {
              required: 'Password is required',
            })}
          />

          <button
            type="button"
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? (
              <EyeOff size={16} />
            ) : (
              <Eye size={16} />
            )}
          </button>
        </div>

        {errors.password && (
          <p
            className="text-xs mt-1"
            style={{ color: 'var(--danger)' }}
          >
            {errors.password.message}
          </p>
        )}
      </div>

      <button
        type="submit"
        disabled={isSubmitting}
        className="btn-primary px-6 py-3 text-sm flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
        style={{ minWidth: '160px' }}
      >
        {isSubmitting ? (
          <>
            <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            SIGNING IN...
          </>
        ) : (
          'SIGN IN'
        )}
      </button>
    </form>

    {showForgotPassword && (
      <ForgotPasswordModal onClose={() => setShowForgotPassword(false)} />
    )}
    </>
  );
}

// ─── Register Form ─────────────────────────────────────────
const MAX_DOCUMENT_BYTES = 5 * 1024 * 1024;

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    if (file.size > MAX_DOCUMENT_BYTES) {
      reject(
        new Error(
          `${file.name} is larger than 5MB. Please upload a smaller file.`
        )
      );
      return;
    }

    const reader = new FileReader();

    reader.onload = () => resolve(reader.result);

    reader.onerror = () =>
      reject(new Error(`Could not read ${file.name}.`));

    reader.readAsDataURL(file);
  });
}

function DocumentUploadField({
  label,
  hint,
  file,
  onChange,
  error,
}) {
  const inputRef = React.useRef(null);

  return (
    <div>
      <label
        className="block text-xs font-heading tracking-stamp mb-1"
        style={{
          fontFamily: 'var(--font-heading)',
        }}
      >
        {label}{' '}
        <span style={{ color: 'var(--primary)' }}>*</span>
      </label>

      {hint && (
        <p
          className="text-xs mb-1.5"
          style={{
            color: 'var(--muted-foreground)',
          }}
        >
          {hint}
        </p>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*,.pdf"
        className="hidden"
        onChange={(e) =>
          onChange(e.target.files?.[0] ?? null)
        }
      />

      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className="input-ledger w-full px-3 py-2.5 text-sm text-left flex items-center justify-between"
      >
        <span
          className="truncate"
          style={{
            color: file
              ? 'var(--foreground)'
              : 'var(--muted-foreground)',
          }}
        >
          {file
            ? file.name
            : 'Choose file (JPG, PNG, or PDF, max 5MB)'}
        </span>

        <Upload
          size={14}
          style={{
            color: 'var(--muted-foreground)',
            flexShrink: 0,
          }}
        />
      </button>

      {error && (
        <p
          className="text-xs mt-1"
          style={{ color: 'var(--danger)' }}
        >
          {error}
        </p>
      )}
    </div>
  );
}

function RegisterForm({ initialRole }) {
  const navigate = useNavigate();

  const [selectedRole, setSelectedRole] =
    useState(initialRole);

  const [step, setStep] = useState('details');

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm();

  const [showPassword, setShowPassword] = useState(false);
  const [regError, setRegError] = useState('');

  // Seller-only KYC document uploads
  const [gstinDocFile, setGstinDocFile] = useState(null);
  const [panDocFile, setPanDocFile] = useState(null);
  const [passbookDocFile, setPassbookDocFile] =
    useState(null);

  const [docErrors, setDocErrors] = useState({});

  const [session, setSession] = useState(null);

  const [emailCode, setEmailCode] = useState('');
  const [verifyError, setVerifyError] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [submittedName, setSubmittedName] = useState('');

  const onSubmitDetails = async (data) => {
    setRegError('');
    setDocErrors({});

    let gstinDocument;
    let panDocument;
    let bankPassbookDocument;

    if (selectedRole === 'seller') {
      const missing = {};

      if (!gstinDocFile) {
        missing.gstin = 'GSTIN certificate is required.';
      }

      if (!panDocFile) {
        missing.pan = 'PAN card is required.';
      }

      if (!passbookDocFile) {
        missing.passbook =
          'Bank passbook or cancelled cheque is required.';
      }

      if (Object.keys(missing).length > 0) {
        setDocErrors(missing);
        return;
      }

      try {
        [
          gstinDocument,
          panDocument,
          bankPassbookDocument,
        ] = await Promise.all([
          readFileAsDataUrl(gstinDocFile),
          readFileAsDataUrl(panDocFile),
          readFileAsDataUrl(passbookDocFile),
        ]);
      } catch (err) {
        setRegError(
          err.message ||
            'Could not read one of your uploaded documents.'
        );
        return;
      }
    }

    try {
      const res = await authApi.registerStart({
        name: data.name,
        email: data.email,
        password: data.password,
        role: selectedRole,
        company:
          selectedRole === 'seller'
            ? data.company
            : undefined,
        city: data.city,

        ...(selectedRole === 'seller'
          ? {
              gstin: data.gstin,
              pan: data.pan,
              bankAccountHolder:
                data.bankAccountHolder,
              bankAccountNumber:
                data.bankAccountNumber,
              bankIfsc: data.bankIfsc,
              gstinDocument,
              panDocument,
              bankPassbookDocument,
            }
          : {}),
      });

      setSession(res);
      setSubmittedName(data.name);
      setStep('verify');

      toast.success(
        'Verification code sent via email.'
      );
    } catch (err) {
      setRegError(
        err.message || 'Could not start registration.'
      );
    }
  };

  const handleVerify = async () => {
    if (emailCode.length !== 6) {
      setVerifyError('Enter the 6-digit code.');
      return;
    }

    setVerifying(true);
    setVerifyError('');

    try {
      const res = await authApi.registerVerify({
        registrationId: session.registrationId,
        emailOtpId: session.email.otpId,
        emailCode,
      });

      if (res.token) {
        storeAuth(res.token, res.user);

        setStep('buyer-approved');

        setTimeout(
          () => navigate('/dashboard'),
          1200
        );
      } else {
        setStep('pending');
      }
    } catch (err) {
      setVerifyError(
        err.message || 'Verification failed.'
      );
    } finally {
      setVerifying(false);
    }
  };

  const handleResend = async () => {
    setResending(true);

    try {
      const res =
        await authApi.registerResend({
          registrationId: session.registrationId,
        });

      setSession((prev) => ({
        ...prev,
        email: {
          otpId: res.otpId,
          masked: res.masked,
        },
      }));

      toast.success('New code sent.');
    } catch (err) {
      toast.error(
        err.message || 'Could not resend code.'
      );
    } finally {
      setResending(false);
    }
  };

  // ── Step 4: Buyer approved ─────────────────────────────
  if (step === 'buyer-approved') {
    return (
      <div className="flex flex-col items-center gap-3 py-6 text-center">
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center"
          style={{
            backgroundColor: 'var(--success-bg)',
          }}
        >
          <Check
            size={20}
            style={{ color: 'var(--secondary)' }}
          />
        </div>

        <p className="text-sm font-medium">
          You're verified,{' '}
          {submittedName.split(' ')[0]}. Taking you to
          your dashboard...
        </p>
      </div>
    );
  }

  // ── Step 3: Pending admin approval ─────────────────────
  if (step === 'pending') {
    return (
      <div className="flex flex-col gap-4">
        <div
          className="p-4 border"
          style={{
            borderColor: 'var(--amber)',
            backgroundColor: 'var(--amber-bg)',
          }}
        >
          <p
            className="text-sm font-medium mb-1"
            style={{ color: 'var(--foreground)' }}
          >
            Application Submitted
          </p>

          <p
            className="text-sm"
            style={{ color: 'var(--foreground)' }}
          >
            Thanks, {submittedName.split(' ')[0]} — your
            email is confirmed, and your seller application
            (including GSTIN, PAN, and bank details) has
            been sent for review.
          </p>
        </div>

        <div
          className="p-4 border"
          style={{
            borderColor: 'var(--secondary)',
            backgroundColor: 'var(--success-bg)',
          }}
        >
          <p
            className="text-sm"
            style={{ color: 'var(--foreground)' }}
          >
            <span className="font-medium">
              Verification typically takes 24–48 hours.
            </span>{' '}
            Once an admin approves your account, you'll be
            able to log in and start listing — no further
            action needed from you in the meantime. We'll
            get in touch if anything needs a closer look.
          </p>
        </div>

        <button
          onClick={() => navigate('/')}
          className="btn-secondary px-4 py-2.5 text-sm"
        >
          BACK TO HOMEPAGE
        </button>
      </div>
    );
  }

  // ── Step 2: Verify email OTP ───────────────────────────
  if (step === 'verify') {
    return (
      <div className="flex flex-col gap-4">
        {verifyError && (
          <div
            className="px-3 py-2 border text-sm"
            style={{
              backgroundColor: 'var(--danger-bg)',
              borderColor: 'var(--danger)',
              color: 'var(--danger)',
            }}
          >
            {verifyError}
          </div>
        )}

        <div>
          <label
            className="block text-xs font-heading tracking-stamp mb-1"
            style={{
              fontFamily: 'var(--font-heading)',
            }}
          >
            EMAIL CODE — sent to{' '}
            {session?.email?.masked}
          </label>

          <div className="flex gap-2">
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={emailCode}
              onChange={(e) =>
                setEmailCode(
                  e.target.value
                    .replace(/\D/g, '')
                    .slice(0, 6)
                )
              }
              className="input-ledger flex-1 px-3 py-2.5 text-sm font-mono-data tracking-widest"
              style={{
                fontFamily: 'var(--font-mono)',
              }}
              placeholder="000000"
            />

            <button
              type="button"
              onClick={handleResend}
              disabled={resending}
              className="btn-secondary px-3 py-2 text-xs whitespace-nowrap disabled:opacity-60"
            >
              {resending ? 'SENDING...' : 'RESEND'}
            </button>
          </div>
        </div>

        <button
          onClick={handleVerify}
          disabled={verifying}
          className="btn-primary px-6 py-3 text-sm flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {verifying ? (
            <>
              <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              VERIFYING...
            </>
          ) : (
            'VERIFY & CONTINUE'
          )}
        </button>

        <button
          type="button"
          onClick={() => setStep('details')}
          className="text-xs underline self-start"
          style={{
            color: 'var(--muted-foreground)',
          }}
        >
          ← Back to details
        </button>
      </div>
    );
  }

  // ── Step 1: Account details ────────────────────────────
  return (
    <form
      onSubmit={handleSubmit(onSubmitDetails)}
      className="flex flex-col gap-4"
    >
      {/* Role Selector */}
      <div>
        <label
          className="block text-xs font-heading tracking-stamp mb-2"
          style={{
            fontFamily: 'var(--font-heading)',
          }}
        >
          I AM A{' '}
          <span style={{ color: 'var(--primary)' }}>*</span>
        </label>

        <div className="grid grid-cols-2 gap-2">
          {['seller', 'buyer'].map((role) => (
            <button
              key={`role-${role}`}
              type="button"
              onClick={() => setSelectedRole(role)}
              className={`px-4 py-3 text-sm flex flex-col items-center gap-1 transition-all duration-150 ${
                selectedRole === role
                  ? 'tab-active'
                  : 'tab-inactive'
              }`}
            >
              {role === 'seller' ? (
                <Package size={18} />
              ) : (
                <ShoppingBag size={18} />
              )}

              <span
                className="font-heading tracking-stamp text-xs"
                style={{
                  fontFamily: 'var(--font-heading)',
                }}
              >
                {role === 'seller' ? 'SELLER' : 'BUYER'}
              </span>

              <span className="text-xs opacity-70">
                {role === 'seller'
                  ? 'List inventory'
                  : 'Source goods'}
              </span>
            </button>
          ))}
        </div>
      </div>

      {regError && (
        <div
          className="px-3 py-2 border text-sm"
          style={{
            backgroundColor: 'var(--danger-bg)',
            borderColor: 'var(--danger)',
            color: 'var(--danger)',
          }}
        >
          {regError}
        </div>
      )}

      {/* Name + City */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label
            className="block text-xs font-heading tracking-stamp mb-1"
            style={{
              fontFamily: 'var(--font-heading)',
            }}
          >
            FULL NAME{' '}
            <span style={{ color: 'var(--primary)' }}>*</span>
          </label>

          <input
            type="text"
            className="input-ledger w-full px-3 py-2.5 text-sm"
            placeholder="Rajesh Kumar"
            {...register('name', {
              required: 'Name is required',
            })}
          />

          {errors.name && (
            <p
              className="text-xs mt-1"
              style={{ color: 'var(--danger)' }}
            >
              {errors.name.message}
            </p>
          )}
        </div>

        <div>
          <label
            className="block text-xs font-heading tracking-stamp mb-1"
            style={{
              fontFamily: 'var(--font-heading)',
            }}
          >
            CITY{' '}
            <span style={{ color: 'var(--primary)' }}>*</span>
          </label>

          <input
            type="text"
            className="input-ledger w-full px-3 py-2.5 text-sm"
            placeholder="Bengaluru"
            {...register('city', {
              required: 'City is required',
            })}
          />

          {errors.city && (
            <p
              className="text-xs mt-1"
              style={{ color: 'var(--danger)' }}
            >
              {errors.city.message}
            </p>
          )}
        </div>
      </div>

      {/* Email */}
      <div>
        <label
          className="block text-xs font-heading tracking-stamp mb-1"
          style={{
            fontFamily: 'var(--font-heading)',
          }}
        >
          EMAIL ADDRESS{' '}
          <span style={{ color: 'var(--primary)' }}>*</span>
        </label>

        <p
          className="text-xs mb-1.5"
          style={{
            color: 'var(--muted-foreground)',
          }}
        >
          We'll send a verification code here — use an
          address you can access now
        </p>

        <input
          type="email"
          className="input-ledger w-full px-3 py-2.5 text-sm"
          placeholder="you@company.in"
          {...register('email', {
            required: 'Email is required',
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: 'Enter a valid email',
            },
          })}
        />

        {errors.email && (
          <p
            className="text-xs mt-1"
            style={{ color: 'var(--danger)' }}
          >
            {errors.email.message}
          </p>
        )}
      </div>

      {/* Seller Company */}
      {selectedRole === 'seller' && (
        <div>
          <label
            className="block text-xs font-heading tracking-stamp mb-1"
            style={{
              fontFamily: 'var(--font-heading)',
            }}
          >
            COMPANY NAME{' '}
            <span style={{ color: 'var(--primary)' }}>*</span>
          </label>

          <p
            className="text-xs mb-1.5"
            style={{
              color: 'var(--muted-foreground)',
            }}
          >
            Registered business name for your listings
          </p>

          <input
            type="text"
            className="input-ledger w-full px-3 py-2.5 text-sm"
            placeholder="Apex Industries Pvt Ltd"
            {...register('company', {
              required:
                selectedRole === 'seller'
                  ? 'Company name is required for sellers'
                  : false,
            })}
          />

          {errors.company && (
            <p
              className="text-xs mt-1"
              style={{ color: 'var(--danger)' }}
            >
              {errors.company.message}
            </p>
          )}
        </div>
      )}

      {/* Seller KYC */}
      {selectedRole === 'seller' && (
        <>
          <div className="pt-1 border-t border-border">
            <p
              className="text-xs font-heading tracking-stamp mt-3 mb-1"
              style={{
                fontFamily: 'var(--font-heading)',
                color: 'var(--primary)',
              }}
            >
              BUSINESS VERIFICATION (KYC) — REVIEWED BY ADMIN
            </p>

            <p
              className="text-xs mb-3"
              style={{
                color: 'var(--muted-foreground)',
              }}
            >
              Required for every seller before your account
              and listings go live.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* GSTIN */}
            <div>
              <label
                className="block text-xs font-heading tracking-stamp mb-1"
                style={{
                  fontFamily: 'var(--font-heading)',
                }}
              >
                GSTIN{' '}
                <span style={{ color: 'var(--primary)' }}>
                  *
                </span>
              </label>

              <input
                type="text"
                className="input-ledger w-full px-3 py-2.5 text-sm font-mono-data uppercase"
                style={{
                  fontFamily: 'var(--font-mono)',
                }}
                placeholder="29ABCDE1234F1Z5"
                maxLength={15}
                {...register('gstin', {
                  required:
                    selectedRole === 'seller'
                      ? 'GSTIN is required'
                      : false,
                  pattern: {
                    value:
                      /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/i,
                    message:
                      'Enter a valid 15-character GSTIN',
                  },
                })}
              />

              {errors.gstin && (
                <p
                  className="text-xs mt-1"
                  style={{ color: 'var(--danger)' }}
                >
                  {errors.gstin.message}
                </p>
              )}
            </div>

            {/* PAN */}
            <div>
              <label
                className="block text-xs font-heading tracking-stamp mb-1"
                style={{
                  fontFamily: 'var(--font-heading)',
                }}
              >
                PAN{' '}
                <span style={{ color: 'var(--primary)' }}>
                  *
                </span>
              </label>

              <input
                type="text"
                className="input-ledger w-full px-3 py-2.5 text-sm font-mono-data uppercase"
                style={{
                  fontFamily: 'var(--font-mono)',
                }}
                placeholder="ABCDE1234F"
                maxLength={10}
                {...register('pan', {
                  required:
                    selectedRole === 'seller'
                      ? 'PAN is required'
                      : false,
                  pattern: {
                    value:
                      /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/i,
                    message:
                      'Enter a valid 10-character PAN',
                  },
                })}
              />

              {errors.pan && (
                <p
                  className="text-xs mt-1"
                  style={{ color: 'var(--danger)' }}
                >
                  {errors.pan.message}
                </p>
              )}
            </div>
          </div>

          {/* Bank Account Holder */}
          <div>
            <label
              className="block text-xs font-heading tracking-stamp mb-1"
              style={{
                fontFamily: 'var(--font-heading)',
              }}
            >
              BANK ACCOUNT HOLDER NAME{' '}
              <span style={{ color: 'var(--primary)' }}>
                *
              </span>
            </label>

            <input
              type="text"
              className="input-ledger w-full px-3 py-2.5 text-sm"
              placeholder="As per bank records"
              {...register('bankAccountHolder', {
                required:
                  selectedRole === 'seller'
                    ? 'Account holder name is required'
                    : false,
              })}
            />

            {errors.bankAccountHolder && (
              <p
                className="text-xs mt-1"
                style={{ color: 'var(--danger)' }}
              >
                {errors.bankAccountHolder.message}
              </p>
            )}
          </div>

          {/* Bank Details */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label
                className="block text-xs font-heading tracking-stamp mb-1"
                style={{
                  fontFamily: 'var(--font-heading)',
                }}
              >
                BANK ACCOUNT NUMBER{' '}
                <span style={{ color: 'var(--primary)' }}>
                  *
                </span>
              </label>

              <input
                type="text"
                inputMode="numeric"
                className="input-ledger w-full px-3 py-2.5 text-sm font-mono-data"
                style={{
                  fontFamily: 'var(--font-mono)',
                }}
                placeholder="000123456789"
                {...register('bankAccountNumber', {
                  required:
                    selectedRole === 'seller'
                      ? 'Account number is required'
                      : false,
                  pattern: {
                    value: /^\d{9,18}$/,
                    message:
                      'Enter a valid account number',
                  },
                })}
              />

              {errors.bankAccountNumber && (
                <p
                  className="text-xs mt-1"
                  style={{ color: 'var(--danger)' }}
                >
                  {errors.bankAccountNumber.message}
                </p>
              )}
            </div>

            <div>
              <label
                className="block text-xs font-heading tracking-stamp mb-1"
                style={{
                  fontFamily: 'var(--font-heading)',
                }}
              >
                BANK IFSC CODE{' '}
                <span style={{ color: 'var(--primary)' }}>
                  *
                </span>
              </label>

              <input
                type="text"
                className="input-ledger w-full px-3 py-2.5 text-sm font-mono-data uppercase"
                style={{
                  fontFamily: 'var(--font-mono)',
                }}
                placeholder="HDFC0000123"
                maxLength={11}
                {...register('bankIfsc', {
                  required:
                    selectedRole === 'seller'
                      ? 'IFSC code is required'
                      : false,
                  pattern: {
                    value:
                      /^[A-Z]{4}0[A-Z0-9]{6}$/i,
                    message:
                      'Enter a valid 11-character IFSC code',
                  },
                })}
              />

              {errors.bankIfsc && (
                <p
                  className="text-xs mt-1"
                  style={{ color: 'var(--danger)' }}
                >
                  {errors.bankIfsc.message}
                </p>
              )}
            </div>
          </div>

          {/* Documents */}
          <DocumentUploadField
            label="GSTIN CERTIFICATE"
            file={gstinDocFile}
            onChange={(f) => {
              setGstinDocFile(f);
              setDocErrors((p) => ({
                ...p,
                gstin: undefined,
              }));
            }}
            error={docErrors.gstin}
          />

          <DocumentUploadField
            label="PAN CARD"
            file={panDocFile}
            onChange={(f) => {
              setPanDocFile(f);
              setDocErrors((p) => ({
                ...p,
                pan: undefined,
              }));
            }}
            error={docErrors.pan}
          />

          <DocumentUploadField
            label="BANK PASSBOOK / CANCELLED CHEQUE"
            hint="Front page showing account number and IFSC, or a cancelled cheque"
            file={passbookDocFile}
            onChange={(f) => {
              setPassbookDocFile(f);
              setDocErrors((p) => ({
                ...p,
                passbook: undefined,
              }));
            }}
            error={docErrors.passbook}
          />
        </>
      )}

      {/* Password */}
      <div>
        <label
          className="block text-xs font-heading tracking-stamp mb-1"
          style={{
            fontFamily: 'var(--font-heading)',
          }}
        >
          PASSWORD{' '}
          <span style={{ color: 'var(--primary)' }}>*</span>
        </label>

        <p
          className="text-xs mb-1.5"
          style={{
            color: 'var(--muted-foreground)',
          }}
        >
          Minimum 8 characters
        </p>

        <div className="relative">
          <input
            type={showPassword ? 'text' : 'password'}
            className="input-ledger w-full px-3 py-2.5 text-sm pr-10"
            placeholder="••••••••"
            {...register('password', {
              required: 'Password is required',
              minLength: {
                value: 8,
                message:
                  'Password must be at least 8 characters',
              },
            })}
          />

          <button
            type="button"
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            onClick={() =>
              setShowPassword(!showPassword)
            }
          >
            {showPassword ? (
              <EyeOff size={16} />
            ) : (
              <Eye size={16} />
            )}
          </button>
        </div>

        {errors.password && (
          <p
            className="text-xs mt-1"
            style={{ color: 'var(--danger)' }}
          >
            {errors.password.message}
          </p>
        )}
      </div>

      {/* Submit */}
      <button
        type="submit"
        disabled={isSubmitting}
        className="btn-primary px-6 py-3 text-sm flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {isSubmitting ? (
          <>
            <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            SENDING VERIFICATION CODES...
          </>
        ) : (
          'CONTINUE TO VERIFICATION'
        )}
      </button>

      <p
        className="text-xs text-center"
        style={{
          color: 'var(--muted-foreground)',
        }}
      >
        You'll verify your email next, then an admin
        reviews your account before you can log in. By
        registering, you agree to our{' '}
        <span
          className="underline cursor-pointer"
          style={{ color: 'var(--primary)' }}
        >
          Terms of Service
        </span>{' '}
        and{' '}
        <span
          className="underline cursor-pointer"
          style={{ color: 'var(--primary)' }}
        >
          Privacy Policy
        </span>
      </p>
    </form>
  );
}

// ─── Stats Ticker ─────────────────────────────────────────
function StatsTicker() {
  const [platformStats, setPlatformStats] =
    useState(null);

  useEffect(() => {
    let cancelled = false;

    statsApi
      .platform()
      .then((data) => {
        if (!cancelled) {
          setPlatformStats(data.stats);
        }
      })
      .catch(() => {
        // Landing page still renders fine without live numbers.
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const s = platformStats || {
    totalUsers: 0,
    gmv: 0,
    dealsClosed: 0,
    activeListings: 0,
  };

  const stats = [
    {
      icon: Users,
      label: 'REGISTERED USERS',
      value: s.totalUsers.toString(),
    },
    {
      icon: TrendingUp,
      label: 'GROSS MERCH VALUE',
      value: formatINR(s.gmv),
    },
    {
      icon: Package,
      label: 'DEALS CLOSED',
      value: s.dealsClosed.toString(),
    },
    {
      icon: ShoppingBag,
      label: 'ACTIVE LISTINGS',
      value: s.activeListings.toString(),
    },
  ];

  return (
    <div className="grid grid-cols-2 gap-3 mt-6">
      {stats.map((stat) => (
        <div
          key={`stat-${stat.label}`}
          className="p-3 border"
          style={{
            borderColor: 'rgba(237,232,220,0.2)',
            backgroundColor:
              'rgba(237,232,220,0.05)',
          }}
        >
          <div className="flex items-center gap-1.5 mb-1">
            <stat.icon
              size={12}
              style={{ color: 'var(--primary)' }}
            />

            <span
              className="text-xs font-heading tracking-stamp opacity-60"
              style={{
                fontFamily: 'var(--font-heading)',
                color: 'var(--background)',
              }}
            >
              {stat.label}
            </span>
          </div>

          <p
            className="text-xl font-heading font-mono-data"
            style={{
              color: 'var(--background)',
              fontFamily: 'var(--font-mono)',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {stat.value}
          </p>
        </div>
      ))}
    </div>
  );
}

// ─── Main Auth Page ────────────────────────────────────────
export default function AuthPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const tabParam = searchParams.get('tab');
  const roleParam = searchParams.get('role');

  const [activeTab, setActiveTab] = useState(
    tabParam === 'register' ? 'register' : 'login'
  );

  const [initialRole] = useState(
    roleParam === 'seller'
      ? 'seller'
      : roleParam === 'buyer'
        ? 'buyer'
        : 'seller'
  );

  // Check if already logged in
  useEffect(() => {
    const token = getStoredToken();

    if (!token) return;

    let cancelled = false;

    authApi
      .me()
      .then(() => {
        if (!cancelled) {
          navigate('/dashboard', {
            replace: true,
          });
        }
      })
      .catch(() => {
        // Token is invalid/expired.
        if (!cancelled) {
          clearAuth();
        }
      });

    return () => {
      cancelled = true;
    };
  }, [navigate]);

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">

      {/* =====================================================
          LEFT PANEL — BRANDING
      ====================================================== */}
      <div
        className="hidden lg:flex lg:w-5/12 xl:w-2/5 flex-col justify-between p-10 xl:p-14 relative overflow-hidden"
        style={{
          backgroundColor: 'var(--foreground)',
        }}
      >
        {/* Background ledger lines */}
        <div className="absolute inset-0 ledger-grid opacity-10" />

        <div className="relative z-10">

          {/* =================================================
              CLEARANCE CONNECT LOGO
              Matches the second image/reference
          ================================================== */}
          <div className="flex items-center gap-4 mb-10">

            {/* Actual Logo Image */}
            <img
              src="/assets/images/Logo1.png"
              alt="Clearance Connect"
              className="h-16 w-auto object-contain"
            />

            {/* Brand Text */}
            <div className="flex flex-col leading-none">
              <span
                className="font-heading text-2xl tracking-stamp"
                style={{
                  color: 'var(--background)',
                  fontFamily: 'var(--font-heading)',
                }}
              >
                CLEARANCE
              </span>

              <span
                className="font-heading text-sm tracking-stamp mt-1"
                style={{
                  color: 'var(--primary)',
                  fontFamily: 'var(--font-heading)',
                }}
              >
                CONNECT
              </span>
            </div>
          </div>

          {/* Main Tagline */}
          <div
            className="border-l-4 pl-4 mb-8"
            style={{
              borderColor: 'var(--primary)',
            }}
          >
            <p
              className="text-3xl xl:text-4xl font-heading leading-tight"
              style={{
                color: 'var(--background)',
                fontFamily: 'var(--font-heading)',
              }}
            >
              CLEAR DEADSTOCK.
            </p>

            <p
              className="text-3xl xl:text-4xl font-heading leading-tight"
              style={{
                color: 'var(--primary)',
                fontFamily: 'var(--font-heading)',
              }}
            >
              UNLOCK VALUE.
            </p>
          </div>

          {/* Description */}
          <p
            className="text-sm leading-relaxed mb-8"
            style={{
              color: 'rgba(237,232,220,0.7)',
            }}
          >
            India's B2B & B2C deadstock clearance
            marketplace. Connect surplus inventory with
            buyers who need it — faster, simpler,
            profitable.
          </p>

          {/* Stats */}
          <StatsTicker />
        </div>

        {/* Footer Branding */}
        <div className="relative z-10">
          <div
            className="inline-flex items-center gap-2 px-3 py-2 border"
            style={{
              borderColor: 'var(--primary)',
              borderStyle: 'dashed',
            }}
          >
            <span
              className="text-xs font-heading tracking-stamp"
              style={{
                color: 'var(--primary)',
                fontFamily: 'var(--font-heading)',
              }}
            >
              ELEVATE 2026 · KARNATAKA STARTUP CELL
            </span>
          </div>

          <p
            className="text-xs mt-2"
            style={{
              color: 'rgba(237,232,220,0.4)',
            }}
          >
            Trivolt Tech Private Limited · Bengaluru
          </p>
        </div>
      </div>

      {/* =====================================================
          RIGHT PANEL — AUTH FORM
      ====================================================== */}
      <div className="flex-1 flex flex-col">

        {/* =================================================
            MOBILE HEADER
        ================================================== */}
        <div
          className="lg:hidden flex items-center justify-between px-4 py-4 border-b-2 border-foreground"
          style={{
            backgroundColor: 'var(--foreground)',
          }}
        >
          {/* Mobile Logo */}
          <div className="flex items-center gap-3">

            <img
              src="/assets/images/Logo1.png"
              alt="Clearance Connect"
              className="h-10 w-auto object-contain"
            />

            <div className="flex flex-col leading-none">
              <span
                className="font-heading text-lg tracking-stamp"
                style={{
                  color: 'var(--background)',
                  fontFamily: 'var(--font-heading)',
                }}
              >
                CLEARANCE
              </span>

              <span
                className="font-heading text-xs tracking-stamp mt-1"
                style={{
                  color: 'var(--primary)',
                  fontFamily: 'var(--font-heading)',
                }}
              >
                CONNECT
              </span>
            </div>

          </div>
        </div>

        {/* =================================================
            AUTH CONTENT
        ================================================== */}
        <div className="flex-1 flex items-center justify-center px-4 py-8 lg:py-12">
          <div className="w-full max-w-md">

            {/* Tab Switcher */}
            <div className="grid grid-cols-2 gap-2 mb-6">

              <button
                onClick={() => setActiveTab('login')}
                className={`py-3 text-sm transition-all duration-150 ${
                  activeTab === 'login'
                    ? 'tab-active'
                    : 'tab-inactive'
                }`}
              >
                LOG IN
              </button>

              <button
                onClick={() => setActiveTab('register')}
                className={`py-3 text-sm transition-all duration-150 ${
                  activeTab === 'register'
                    ? 'tab-active'
                    : 'tab-inactive'
                }`}
              >
                REGISTER
              </button>

            </div>

            {/* Form Header */}
            <div className="mb-6">
              <h2
                className="font-heading text-2xl tracking-stamp"
                style={{
                  fontFamily: 'var(--font-heading)',
                }}
              >
                {activeTab === 'login'
                  ? 'WELCOME BACK'
                  : 'CREATE ACCOUNT'}
              </h2>

              <p
                className="text-sm mt-1"
                style={{
                  color: 'var(--muted-foreground)',
                }}
              >
                {activeTab === 'login'
                  ? 'Sign in to your ClearanceConnect account'
                  : 'Join the B2B & B2C deadstock clearance platform'}
              </p>
            </div>

            {/* Form */}
            {activeTab === 'login' ? (
              <LoginForm />
            ) : (
              <RegisterForm
                initialRole={initialRole}
              />
            )}

            {/* Switch Login / Register */}
            <p
              className="text-sm text-center mt-4"
              style={{
                color: 'var(--muted-foreground)',
              }}
            >
              {activeTab === 'login' ? (
                <>
                  New to ClearanceConnect?{' '}

                  <button
                    onClick={() =>
                      setActiveTab('register')
                    }
                    className="font-medium underline"
                    style={{
                      color: 'var(--primary)',
                    }}
                  >
                    Create an account
                  </button>
                </>
              ) : (
                <>
                  Already registered?{' '}

                  <button
                    onClick={() =>
                      setActiveTab('login')
                    }
                    className="font-medium underline"
                    style={{
                      color: 'var(--primary)',
                    }}
                  >
                    Sign in
                  </button>
                </>
              )}
            </p>
          </div>
        </div>

        {/* Footer */}
        <AppFooter />
      </div>

      {/* Toast Notifications */}
      <Toaster
        position="bottom-right"
        richColors
      />
    </div>
  );
}